import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    input,
    output,
    ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { SvgIconComponent } from 'angular-svg-icon';
import { ButtonDirective } from '@directives/button.directive';
import { DestroyService } from '@services/destroy.service';
import { animate, style, transition, trigger } from '@angular/animations';
import { DialogService } from '@services/dialog.service';
import { ToastService } from '@services/toast.service';
import { NotificationService } from '@services/notification.service';
import { debounceTime, distinctUntilChanged, filter, fromEvent, map, takeUntil } from 'rxjs';
import {
    ChangeStatusNotification,
    NotificationActionEnum,
    NotificationModel,
    NotificationStatusEnum,
    NotificationTypeEnum,
} from '@models/notification.model';
import { RelativeTimePipe } from '@pipes/relative-time.pipe';
import { NotificationDetailDialogComponent } from '@core/layout/navigation/notification/dialog/notification-detail-dialog/notification-detail-dialog.component';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Router } from '@angular/router';

@Component({
    selector: 'pn-notification-list',
    standalone: true,
    imports: [CommonModule, SvgIconComponent, ButtonDirective, RelativeTimePipe],
    templateUrl: './notification-list.component.html',
    styleUrl: './notification-list.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [DestroyService],
    animations: [
        trigger('showState', [
            transition(':enter', [style({ opacity: 0 }), animate('300ms', style({ opacity: 1 }))]),
            transition(':leave', [animate('300ms', style({ opacity: 0 }))]),
        ]),
    ],
})
export class NotificationListComponent implements AfterViewInit {
    @ViewChild('scrollViewport') scrollViewport!: ElementRef<HTMLDivElement>;
    public notifications = input<NotificationModel[]>([]);
    public loading = input(false);
    public readonly allNotificationsLoaded = input(false);
    public itemChange = output<void>();
    public scrollToBottom = output<boolean>();
    public notificationStatusEnum = NotificationStatusEnum;
    public notificationTypeEnum = NotificationTypeEnum;

    constructor(
        private readonly _destroyService: DestroyService,
        private readonly _dialogService: DialogService,
        private readonly _notificationService: NotificationService,
        private readonly _toastService: ToastService,
        private readonly _cd: ChangeDetectorRef,
        private readonly _device: DeviceDetectorService,
        private readonly _router: Router
    ) {}

    ngAfterViewInit(): void {
        if (this.scrollViewport.nativeElement) {
            fromEvent(this.scrollViewport.nativeElement, 'scroll')
                .pipe(
                    debounceTime(20),
                    filter(() => !this.allNotificationsLoaded()),
                    map((event) => {
                        const { scrollHeight, offsetHeight, scrollTop } =
                            event.target as HTMLElement;
                        return scrollTop + offsetHeight >= scrollHeight;
                    }),
                    distinctUntilChanged(),
                    takeUntil(this._destroyService)
                )
                .subscribe((value) => {
                    this.scrollToBottom.emit(value);
                });
        }
    }

    /**
     * Track by function
     *
     * @param {number} _index
     * @param {NotificationModel} item
     * @return {*}
     * @memberof NotificationListComponent
     */
    public trackByNotificationId(_index: number, item: NotificationModel): number {
        return item.NotificationId;
    }

    /**
     * Check condition show interact btn
     *
     * @param {NotificationModel} item
     * @return {*}
     * @memberof NotificationListComponent
     */
    public isShowInteract(item: NotificationModel): boolean {
        const isInteractOrderDetail = [
            NotificationActionEnum.OrderDetail,
            NotificationActionEnum.CreateOrder,
        ].includes(item.Action as NotificationActionEnum);

        return (
            isInteractOrderDetail &&
            item.TypeNotification === NotificationTypeEnum.Interact &&
            !!item.Data?.TransactionId
        );
    }

    /**
     * Accept or deny credit request
     *
     * @param {NotificationModel} item
     * @param event
     * @memberof NotificationListComponent
     */
    public interactNotification(item: NotificationModel, event: MouseEvent) {
        event.preventDefault();
        event.stopPropagation();
        const { Data } = item;
        if (this.isShowInteract(item) && Data?.TransactionId) {
            this._router.navigate(['/flight/search/payment/', Data.TransactionId], {
                queryParams: {
                    isRedirect: false,
                },
            });
        }
    }

    /**
     * Handle show detail
     *
     * @param {NotificationModel} item
     * @memberof NotificationListComponent
     */
    public handleShowDetail(item: NotificationModel) {
        if (!item.Status) {
            this._handleChangeStatusNotification(item);
        }

        this._dialogService.openDialogWithCondition(
            NotificationDetailDialogComponent,
            this._device.isMobile(),
            item
        );
    }

    /**
     * Handle change status notification
     *
     * @private
     * @param {NotificationModel} item
     * @memberof NotificationListComponent
     */
    private _handleChangeStatusNotification(item: NotificationModel) {
        if (item.Status !== NotificationStatusEnum.UnRead) {
            return;
        }
        const req: ChangeStatusNotification = {
            ListNotificationId: [item.NotificationId],
            Status: NotificationStatusEnum.Read,
        };
        this._notificationService
            .changeStatusNotification(req)
            .pipe(takeUntil(this._destroyService))
            .subscribe(() => {
                item.Status = NotificationStatusEnum.Read;
                this._cd.detectChanges();
            });
    }
}
